import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnDestroy, inject } from "@angular/core";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { Observable, Subject, combineLatest } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { EntityType } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VkAtgardEntity } from "../../models/atgard-models";
import { Kontrakt } from "../../models/kontrakt-models";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { UtskriftMediatorService } from "../../services/utskrift-mediator.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { KontraktAnteckningskogsvardComponent } from "./kontrakt-anteckningskogsvard/kontrakt-anteckningskogsvard.component";
import { KontraktJusteraUtskriftComponent } from "./kontrakt-justera-utskrift/kontrakt-justera-utskrift.component";

@Component({
    selector: "rask-kontrakt-utskrift",
    templateUrl: "./kontrakt-utskrift.component.html",
    styleUrls: ["./kontrakt-utskrift.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TogglePanelComponent,
        KontraktJusteraUtskriftComponent,
        KontraktAnteckningskogsvardComponent,
        AsyncPipe
    ]
})
export class KontraktUtskriftComponent implements OnDestroy {
    protected readonly isSkogsvardVisible$: Observable<boolean>;
    protected readonly isVisible$: Observable<boolean>;
    protected kontrakt: Kontrakt;
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);

    constructor() {
        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(k => {
                this.kontrakt = k;
            });

        const { kontrakt$, atgarderPreload$ } = this.kontraktMediatorService;
        this.isVisible$ = combineLatest([
            kontrakt$,
            atgarderPreload$
        ]).pipe(
            map(([
                kontrakt,
                atgarder
            ]) =>
                this.visible(kontrakt, atgarder))
        );

        this.isSkogsvardVisible$ = atgarderPreload$.pipe(
            map(atgarder => this.skogsvardVisible(atgarder))
        );
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    protected getIcon(): string {
        return this.validationService.getEntitySeverity("utskrift", this.kontrakt?.guid);
    }

    protected toggle(toggled: boolean) {
        if (toggled) {
            this.utskriftMediatorService.panelExpanded$.next();
        }
    }

    private skogsvardVisible(atgarder: VkAtgardEntity[]) {
        return atgarder.some(x => x.type === EntityType.virkeskopsAtgardSkogsvard);
    }

    private visible(kontrakt: Kontrakt, atgarder: any[]) {
        return ObjectUtils.hasValue(kontrakt?.id) && atgarder.length > 0;
    }
}
